import type { DataResponseDTO } from "@eu.mdotm.api/smart-workspaces";
import { Text } from "@mdotm/shadcn/legacy/components";
import { useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { typedObjectKeys } from "@mdotm/shadcn/legacy/utils";
import { Skeleton } from "@mdotm/shadcn/ui";
import { ForEach, useMandatoryContext } from "@mdotm/utils/react-extra";
import { noop } from "@mdotm/utils/runtime";
import { useQueries } from "@tanstack/react-query";
import { Set } from "immutable";
import { useMemo, useState } from "react";
import { type SortDirection, SortDropdown } from "$root/components/SortDropdown";
import { userClassificationsMap$ } from "$root/functional-areas/instruments/user-classifications";
import { chartsStale, generateColumnColor } from "$root/functional-areas/portfolio-lab/common";
import { type GetPortfolioLabChartItem, PortfolioLabContext } from "$root/functional-areas/portfolio-lab/ctx";
import { useLabSession } from "$root/functional-areas/portfolio-lab/hooks/useLabSession";
import { portfolioLabRepository } from "$root/functional-areas/portfolio-lab/repository";
import { useLocaleFormatters } from "$root/localization/hooks";
import {
	PortfolioQueryWidgetBase,
	type PortfolioWidgetData,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { useMappedDynamicQueries } from "$root/third-party-integrations/react-query/hooks";
import { Card } from "$root/widgets-architecture/layout/Card";
import {
	ExposureCategorySelector,
	type ExposureCategoryState,
	fetchExposureCategories,
	WithExposureCategories,
} from "$root/widgets-architecture/widgets/ExposureEvolve";
import type { InvestmentExposureResponseExposureTypeEnum } from "$root/widgets-architecture/widgets/ExposureEvolve/InvestmentExposureResponseExposureTypeEnum";
import { MetricsBlurOverlay } from "../MetricsBlurOverlay";

/** @internal — exported for tests */
export type ExposureRow = {
	secondLevel: string;
	byVirtualUuid: Record<string, number | undefined>;
};

type ExposureGridViewProps = {
	exposureData: ExposureRow[];
	decimals: number;
	sortDirection: SortDirection;
};

export function ExposureGrid({
	chartConfig,
}: {
	chartConfig: GetPortfolioLabChartItem<"EXPOSURE">;
}): React.JSX.Element {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const investments = useAtomValue(ctx.investments$);
	const { saveLabSession } = useLabSession();

	const [sortDirection, setSortDirection] = useState<SortDirection>(undefined);

	const upstreamPreference = chartConfig.preference;

	const [exposureCategory, setExposureCategoryState] = useState<ExposureCategoryState<AllowedExposureCategories>>(
		() =>
			({
				firstLevel: upstreamPreference?.firstLevel ?? "CUSTOM_TAG",
				secondLevel: upstreamPreference?.secondLevel ?? null,
			}) as ExposureCategoryState<AllowedExposureCategories>,
	);

	function setExposureCategory(category: ExposureCategoryState<AllowedExposureCategories>) {
		setExposureCategoryState(category);
		writeAtom(ctx.charts$, (cs) =>
			cs.map((c) =>
				c.uuid === chartConfig.uuid
					? {
							...c,
							type: "EXPOSURE" as const,
							preference: {
								firstLevel: category.firstLevel,
								secondLevel: category.secondLevel,
							},
						}
					: c,
			),
		);
		saveLabSession();
	}

	const classificationMap = useAtomValue(userClassificationsMap$);
	const queriesRaw = useQueries({
		queries: investments.map((inv) => ({
			...portfolioLabRepository.exposure.getQueryConfig(
				inv.virtualUuid,
				exposureCategory.firstLevel === "CUSTOM_TAG"
					? {
							type: "TAG",
							label: classificationMap?.get(exposureCategory.secondLevel ?? "")?.name ?? "",
							uuid: exposureCategory.secondLevel!,
						}
					: {
							type: "RISK_MODEL",
							label: exposureCategories[exposureCategory.secondLevel!].label,
							uuid: exposureCategories[exposureCategory.secondLevel!].value,
						},
			),
			keepPreviousData: true,
			enabled: exposureCategory.secondLevel != null,
		})),
	});

	const exposureQuery = useMappedDynamicQueries(
		(results: DataResponseDTO[]): PortfolioWidgetData<ExposureRow[]> => {
			const perInvBySecondLevel: Record<string, Record<string, number>> = {};
			const secondLevelOrder: string[] = [];

			investments.forEach((inv, i) => {
				const raw = results[i]?.items?.[0]?.dictionary?.["a4dd4e90-680e-4c3a-87f1-4f6a097e2b09"]?.value as
					| { buckets?: { option: string; weight: number }[] }
					| undefined;
				const buckets = raw?.buckets;
				if (!buckets?.length) {
					return;
				}
				for (const bucket of buckets) {
					const { option: label, weight } = bucket;
					if (!perInvBySecondLevel[label]) {
						perInvBySecondLevel[label] = {};
						secondLevelOrder.push(label);
					}
					// Keyed by virtualUuid, not the source uuid: the same investment can be loaded twice
					// (two lab columns, same source), and source-uuid keys would collapse them into one series.
					perInvBySecondLevel[label][inv.virtualUuid] = weight;
				}
			});

			const rows: ExposureRow[] = secondLevelOrder.map((label) => ({
				secondLevel: label,
				byVirtualUuid: Object.fromEntries(
					investments.map((inv) => [inv.virtualUuid, perInvBySecondLevel[label][inv.virtualUuid]]),
				),
			}));

			if (rows.length === 0) {
				return { widgetStatus: WidgetStatus.EMPTY, data: rows };
			}
			return { widgetStatus: WidgetStatus.READY, data: rows };
		},
		[investments],
		queriesRaw,
	);

	const title = "Exposure";

	// While a column's charts are stale, the displayed breakdown belongs to the server's last computed
	// composition, and switching category would refetch against that same stale state. Lock both
	// levels until the recompute lands.
	const outOfSync = investments.some(chartsStale);

	return (
		<MetricsBlurOverlay
			qualifier="PortfolioLab/Chart(EXPOSURE)"
			enableBlur={exposureCategory.secondLevel !== null && outOfSync}
		>
			<Card
				title={
					<>
						<Text type="Body/XL/Bold" title={title} className="truncate shrink-0">
							{title}
						</Text>
						<ExposureCategorySelectorLoader
							exposureCategory={exposureCategory}
							setExposureCategory={setExposureCategory}
							upstreamHasPreference={upstreamPreference?.secondLevel != null}
							disabled={outOfSync}
						/>
					</>
				}
				actionHeader={<SortDropdown direction={sortDirection} onChange={setSortDirection} />}
			>
				<PortfolioQueryWidgetBase query={exposureQuery}>
					{(exposureData) => (
						<ExposureGridView exposureData={exposureData} decimals={2} sortDirection={sortDirection} />
					)}
				</PortfolioQueryWidgetBase>
			</Card>
		</MetricsBlurOverlay>
	);
}

type AllowedExposureCategories = Extract<
	InvestmentExposureResponseExposureTypeEnum,
	"MACRO_ASSET_CLASS" | "MICRO_ASSET_CLASS" | "MACRO_GEOGRAPHY" | "MICRO_GEOGRAPHY" | "CURRENCY"
>;
const exposureCategories = {
	MACRO_ASSET_CLASS: {
		value: "macroAssetClass",
		label: "Macro Asset Class",
	},
	MICRO_ASSET_CLASS: {
		value: "microAssetClass",
		label: "Micro Asset Class",
	},
	MACRO_GEOGRAPHY: {
		value: "macroGeography",
		label: "Macro Geography",
	},
	MICRO_GEOGRAPHY: {
		value: "microGeography",
		label: "Micro Geography",
	},
	CURRENCY: {
		value: "currency",
		label: "Currency",
	},
} satisfies Record<AllowedExposureCategories, { label: string; value: string }>;
const allowedExposureCategoriesSet: Immutable.Set<string> = Set(
	typedObjectKeys(exposureCategories) satisfies Array<AllowedExposureCategories>,
);

function ExposureCategorySelectorLoader<
	TExposureRiskModelCategories extends
		InvestmentExposureResponseExposureTypeEnum = InvestmentExposureResponseExposureTypeEnum,
>({
	exposureCategory,
	setExposureCategory,
	upstreamHasPreference,
	disabled,
}: {
	exposureCategory: ExposureCategoryState<TExposureRiskModelCategories>;
	setExposureCategory: (category: ExposureCategoryState<TExposureRiskModelCategories>) => void;
	upstreamHasPreference: boolean;
	disabled: boolean;
}): React.JSX.Element {
	return (
		<WithExposureCategories
			queryKey={["lab", "allowedExposureCategories"]}
			queryFn={async () => {
				const categories = await fetchExposureCategories(allowedExposureCategoriesSet);
				return { ...categories };
			}}
			loadingFallback={
				<div className="flex gap-2">
					<Skeleton className="h-8 w-32" />
					<Skeleton className="h-8 w-40" />
				</div>
			}
			onSuccess={(options) =>
				!upstreamHasPreference && options.fromTagClassifications.at(0)?.value
					? setExposureCategory({
							firstLevel: "CUSTOM_TAG",
							secondLevel: options.fromTagClassifications.at(0)!.value,
						})
					: noop()
			}
		>
			{(exposureCategories) => (
				<ExposureCategorySelector
					size="x-small"
					className="min-w-[280px]"
					exposureCategory={exposureCategory}
					setExposureCategory={setExposureCategory}
					exposureCategories={exposureCategories}
					disabled={disabled}
					triggerDataAttrs={{
						firstLevel: { "data-qualifier": "PortfolioLab/Exposure/MacroGroup" },
						secondLevel: { "data-qualifier": "PortfolioLab/Exposure/SubGroup" },
					}}
				/>
			)}
		</WithExposureCategories>
	);
}

/** Combined exposure magnitude of a row across every loaded investment (missing weights count as 0). */
/** @internal — exported for tests */
export function exposureRowTotalWeight(row: ExposureRow): number {
	return Object.values(row.byVirtualUuid).reduce<number>((sum, weight) => sum + (weight ?? 0), 0);
}

/** Sorts rows by their total exposure weight; returns the original order when no direction is selected. */
/** @internal — exported for tests */
export function sortExposureRows(rows: ExposureRow[], direction: SortDirection): ExposureRow[] {
	if (!direction) {
		return rows;
	}
	const sorted = [...rows].sort((a, b) => exposureRowTotalWeight(a) - exposureRowTotalWeight(b));
	if (direction === "DESC") {
		sorted.reverse();
	}
	return sorted;
}

function ExposureGridView({ exposureData, decimals, sortDirection }: ExposureGridViewProps) {
	const { formatNumber } = useLocaleFormatters();
	const virtualUuids = Array.from(Set(exposureData.flatMap((row) => Object.keys(row.byVirtualUuid))));
	const colorByVirtualUuid: Record<string, string> = Object.fromEntries(
		virtualUuids.map((virtualUuid, i) => [virtualUuid, generateColumnColor(i)]),
	);
	const sortedRows = useMemo(() => sortExposureRows(exposureData, sortDirection), [exposureData, sortDirection]);

	return (
		<div className="overflow-y-auto max-h-[300px] min-h-[200px]">
			<ForEach collection={sortedRows} keyProvider={(row) => row.secondLevel}>
				{(row) => (
					<div className="grid grid-cols-[30%_1fr] items-center border-b border-gray-100 last:border-b-0">
						<Text as="div" type="Body/S/Bold" className="py-1.5 pl-3 pr-2.5 whitespace-nowrap overflow-hidden">
							{row.secondLevel}
						</Text>
						<div
							className="grid py-[3px] pr-3"
							style={{
								gridTemplateRows: `repeat(${virtualUuids.length}, 1fr)`,
							}}
						>
							<ForEach collection={virtualUuids} keyProvider={(virtualUuid) => virtualUuid}>
								{(virtualUuid) => {
									const value = row.byVirtualUuid[virtualUuid];
									const color = colorByVirtualUuid[virtualUuid] ?? "#33C8FF";
									return (
										<div
											className="flex items-center gap-[5px] py-[3px]"
											data-qualifier={`PortfolioLab/Exposure/Value(${row.secondLevel},${virtualUuid})`}
										>
											<div
												className="h-1.5 max-w-full min-w-px rounded-[2px] opacity-80"
												data-column-color={color}
												style={{
													width: `${value ?? 0}%`,
													background: color,
												}}
											/>
											<Text type="Body/S/Book" className="whitespace-nowrap">
												{value == null ? "N/A" : `${formatNumber(value, decimals)}%`}
											</Text>
										</div>
									);
								}}
							</ForEach>
						</div>
					</div>
				)}
			</ForEach>
		</div>
	);
}
